<script setup>
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'
import { useStore } from 'vuex'
import {
  KkTextField,
  KkValidationError,
  KkFormLabel,
  KkCheckbox
} from '../../../../components/base'
import { KkInfo } from '../../../../components/ui'
import MediaImage from '../../../../components/MediaImage'

const store = useStore()
const emit = defineEmits(['save', 'cancel'])
const { t } = useI18n()
const loading = computed(() => store.state.companiesMediaImage.loading)
const state = reactive({
  mediaImage: null,
  file: null,
  deleteIcon: false
})
const props = defineProps({
  name: { type: String, default: '' },
  icon: { type: String, default: '' },
  id: Number,
  isPublished: { type: Boolean, default: true },
  isDisplayIcon: { type: Boolean, default: false },
})

const schema = {
  name: 'required|max:100',
}

const { handleSubmit, meta, setFieldValue } = useForm({
  validationSchema: schema,
  initialValues: {
    name: props.name,
    isPublished: props.isPublished
  },
})

const { value: name, errorMessage: nameError, handleChange: nameHandleChange } = useField('name', undefined, { label: t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.name') })
const { value: isPublished } = useField('isPublished')

const saveConditions = handleSubmit( async (value) => {
  const payload = { name: value.name, isPublished: value.isPublished, url: props.icon }
  if(state.mediaImage && state.file) {
    payload.url = state.mediaImage.url
    payload.icon = { content: await readAsDataURL(state.file), filename: state.mediaImage.name }
  }
  if(state.deleteIcon) payload.icon = null
  if(props.id) payload.id = props.id
  emit('save', payload)
})

const onDropMediaImage = async (images) => {
  state.file = images[0]
  state.mediaImage = { id: 1, name: state.file.name, url: window.URL.createObjectURL(state.file) }
  state.deleteIcon = false
}

const onRemoveMediaImage = () => {
  state.mediaImage = null
  state.file = null
  state.deleteIcon = true
}

const readAsDataURL = (blob) => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader()
    reader.onload = () => { resolve(reader.result) }
    reader.onerror = () => { reject(reader.error) }
    reader.readAsDataURL(blob)
  })
}

const validation = computed(() => {
  let valid = meta.value.valid
  if(props.isDisplayIcon) valid = meta.value.valid && !!state.mediaImage
  return valid
})

const initData = () => {
  if(props.icon) state.mediaImage = { id: 1, name: 'icon', url: props.icon }
}
initData()
</script>
<template>
  <div class="p-box">
    <div class="p-box__body">
      <div class="formContainer">
        <KkFormLabel :text="`${ t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.name') }(${ $t('labels.countCharacters', { count: name.length, max: 100 }) })${ t('labels.requiredLabel') }`" />
        <KkTextField :model-value="name" class="c-textField" :placeholder="$t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.placeholder')" @update:model-value="nameHandleChange" />
        <p>{{ $t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.example') }}</p>
        <KkValidationError :text="nameError" />
      </div>
      <div v-if="isDisplayIcon" class="formContainer">
        <MediaImage
          class="form"
          :max-files="1"
          :images="state.mediaImage ? [state.mediaImage] : []"
          :loading="loading"
          :label-detail="t('labels.requiredLabel')"
          rules="image|size:5000|ext:jpeg,jpg,png,gif"
          @drop-images="onDropMediaImage"
          @remove-image="onRemoveMediaImage"
        />
        <KkInfo icon="hint">
          {{ t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.info') }}
        </KkInfo>
      </div>
      <div class="formContainer">
        <KkCheckbox
          id="isPublished"
          :model-value="isPublished"
          :label="$t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.isPublished')"
          @change="setFieldValue('isPublished', !isPublished)"
        />
      </div>
      <div class="c-button c-button--right">
        <button class="c-button__item c-button__item--outline-cancel" @click="emit('cancel')">
          {{ $t('labels.cancel') }}
        </button>
        <button
          class="c-button__item c-button__item--primary"
          :class="{'c-button__item--disabled': !validation}" :disabled="!validation"
          @click="saveConditions()"
        >
          {{ $t('labels.submitFilterCond') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.box {
  display: flex;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  padding: 24px;
  &__header {
    display: flex;
    justify-content: space-between;
  }
  &__title {
    width: 100%;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 16px;
  }
}

.formContainer {
  margin-bottom: 15px;
}

.menu {
  width: 5%;
  &__editButton {
    display: flex;
    cursor: pointer;
    position: relative;
  }

  &__editList {
    position: absolute;
    width: 240px;
    background: white;
    box-shadow: 0 1px 4px rgb(0 0 0 / 26%);
    padding: 10px 0;
    z-index: 1;
    right: 0;
  }

  &__editListItem{
    margin-bottom: 5px;
    padding: 20px 24px;
    &:hover {
      background-color: rgba(0,0,0,.1);
      cursor: pointer;
    }
  }
}

.content {
  width: 95%;
}
.bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
